import { FC, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/_molecules/combobox/combobox";
import { ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { DataSource } from "@/domain/energy-source/lib/data-source-quality-mapping";

import { DataSourceSelectDropdownItem } from "./data-source-select-dropdown-item";
import { DataSourceViewEmpty } from "./data-source-view-empty";
import { EMISSION_FACTOR_DATA_SOURCE_LIST } from "./right-aside-data-source-view";

interface DataSourceSelectDropdownProps extends PropsWithChildren {
  onSelect: (dataSource: Pick<DataSource, "id" | "title"> | null) => void;
  selectedOption: DataSource | null;
  asChild: boolean;
  isReadOnly: boolean;
  // Selectable sources; defaults to the energy-consumption list. Pass a different
  // list (e.g. DIRECT_EMISSION_FACTOR_DATA_SOURCE_LIST) to offer context-specific
  // sources such as IPCC AR6.
  dataSourceList?: DataSource[];
}

export const DataSourceSelectDropdown: FC<DataSourceSelectDropdownProps> = ({
  onSelect,
  selectedOption,
  asChild,
  isReadOnly,
  children,
  dataSourceList = EMISSION_FACTOR_DATA_SOURCE_LIST,
}: DataSourceSelectDropdownProps) => {
  const { t } = useTranslation("common");
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(selectedOption ?? null);

  useEffect(() => {
    setSelectedDataSource(selectedOption ?? null);
  }, [selectedOption]);

  const comboboxOptions = useMemo(() => {
    const optionList = dataSourceList.slice().flatMap((dataSource) => {
      if (!dataSource) {
        return [];
      }
      return {
        id: dataSource.id,
        value: dataSource.title,
        original: dataSource,
      };
    });
    return [{ id: "-1", value: t("notAssigned"), original: null }, ...optionList];
  }, [t, dataSourceList]);

  const renderItem = (option: ComboboxOption<DataSource>): ReactElement => {
    const dataSource = option.original;
    if (!dataSource) {
      return <DataSourceViewEmpty />;
    }

    return (
      <DataSourceSelectDropdownItem
        dataSource={dataSource.title}
        isSelected={selectedDataSource?.id === dataSource.id}
      />
    );
  };

  const handleUserSelected = (selectedOption: ComboboxOption<DataSource> | null) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.id === "-1") {
      setSelectedDataSource(null);
      onSelect(null);
      return;
    }

    if (isReadOnly) {
      return;
    }

    setSelectedDataSource(selectedOption.original);
    onSelect(selectedOption.original);
  };

  return (
    <Combobox
      key={selectedDataSource?.id}
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
    >
      {children}
    </Combobox>
  );
};
